import type { Track } from './types/scenario';
import type { WaypointAttributes } from './types/scenario';
import {
  calculateBearing,
  haversineDistance,
} from './generation/interpolation';

/**
 * Track prediction (dead-reckoning extrapolation).
 *
 * Mirrors ais-main's `EcMonitorCalculatePrediction`: project a vessel forward
 * from its last known position using great-circle dead reckoning.
 *
 * Two flavours, matching ais-main:
 * - **SOG vector** — a straight line along the current course (COG), length
 *   `SOG × duration`. Shows where the vessel will be if it holds course/speed.
 * - **Predictor** — a curved path that also integrates the rate of turn (ROT),
 *   so the line bends the way the vessel is turning.
 */

/** Metres per nautical mile. */
const METERS_PER_NM = 1852;
/** Earth's mean radius in metres (matches haversineDistance). */
const EARTH_RADIUS_M = 6371008.8;

/** Default predictor (ROT curve) duration in seconds — matches ais-main. */
export const DEFAULT_PREDICTOR_DURATION_SEC = 360;
/** Default SOG vector (straight) duration in seconds — matches ais-main. */
export const DEFAULT_SOG_VECTOR_DURATION_SEC = 1800;

/** Approximate seconds between sampled points along the predictor curve. */
const PREDICTOR_STEP_SEC = 15;
const PREDICTOR_MIN_STEPS = 8;
const PREDICTOR_MAX_STEPS = 240;

/**
 * AIS feeds rarely include rate of turn, so we derive an effective turn rate
 * from how the course (segment bearing) changes over time. These bound that
 * derivation: only segments within the window are used, very short segments are
 * ignored as direction noise, and the result is clamped to a sane magnitude.
 */
const ROT_DERIVATION_WINDOW_SEC = 600;
const ROT_MIN_SEGMENT_M = 10;
const ROT_MAX_DEG_MIN = 60;

/** A coordinate as [longitude, latitude] in WGS84 degrees. */
export type LonLat = [number, number];

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/**
 * Great-circle destination point: starting at [lon, lat], travel `distanceM`
 * metres along the initial `bearingDeg` (0 = north, clockwise).
 */
export function destinationPoint(
  lon: number,
  lat: number,
  bearingDeg: number,
  distanceM: number,
): LonLat {
  const angDist = distanceM / EARTH_RADIUS_M;
  const brng = toRad(bearingDeg);
  const lat1 = toRad(lat);
  const lon1 = toRad(lon);

  const lat2 = Math.asin(
    Math.sin(lat1) * Math.cos(angDist) +
      Math.cos(lat1) * Math.sin(angDist) * Math.cos(brng),
  );
  const lon2 =
    lon1 +
    Math.atan2(
      Math.sin(brng) * Math.sin(angDist) * Math.cos(lat1),
      Math.cos(angDist) - Math.sin(lat1) * Math.sin(lat2),
    );

  // Normalise longitude to [-180, 180].
  const lonDeg = ((toDeg(lon2) + 540) % 360) - 180;
  return [lonDeg, toDeg(lat2)];
}

/** The motion state used to seed a prediction, taken from the track's tail. */
interface CurrentState {
  lon: number;
  lat: number;
  /** Course over ground in degrees (0-360). */
  cogDeg: number;
  /** Speed over ground in knots. */
  sogKn: number;
  /** Rate of turn in degrees per minute (signed; positive = turning starboard). */
  rotDegMin: number;
  /** Whether the rate of turn was derived from course history. */
  rotDerived: boolean;
}

/**
 * The motion values a prediction is based on, for display/explanation in the UI
 * (e.g. a hover tooltip). `rotDerived` is true when the rate of turn was
 * estimated from the course history rather than supplied by the data.
 */
export interface PredictionState {
  /** Course over ground in degrees (0-360). */
  cogDeg: number;
  /** Speed over ground in knots. */
  sogKn: number;
  /** Rate of turn in degrees per minute (signed; positive = starboard). */
  rotDegMin: number;
  /** Whether the rate of turn was derived from course history. */
  rotDerived: boolean;
}

/**
 * Expose the motion state a prediction would be built from, or null when it
 * can't be computed (track too short, or vessel stopped).
 */
export function getPredictionState(track: Track): PredictionState | null {
  const state = getCurrentState(track);
  if (!state) return null;
  const { cogDeg, sogKn, rotDegMin, rotDerived } = state;
  return { cogDeg, sogKn, rotDegMin, rotDerived };
}

/**
 * Course over ground (degrees) from the track's tail, using the most recent
 * segment long enough to give a reliable bearing. Very short segments are
 * skipped as GPS/AIS jitter; if every recent segment is tiny we fall back to
 * the final segment rather than give up.
 */
function deriveCurrentCourse(coords: number[][]): number {
  for (let i = coords.length - 1; i >= 1; i--) {
    const [lon1, lat1] = coords[i - 1];
    const [lon2, lat2] = coords[i];
    if (haversineDistance(lon1, lat1, lon2, lat2) >= ROT_MIN_SEGMENT_M) {
      return calculateBearing(lon1, lat1, lon2, lat2);
    }
  }
  const last = coords.length - 1;
  return calculateBearing(
    coords[last - 1][0],
    coords[last - 1][1],
    coords[last][0],
    coords[last][1],
  );
}

/**
 * Derive the current motion state from the end of a track.
 *
 * Position is the last coordinate; course is the bearing of the most recent
 * non-trivial segment (course made good). Speed and rate of turn come from the
 * most recent waypoint that carries a usable value — the rate of turn is
 * derived from the course history when the data doesn't supply one. Returns
 * null when the track is too short or has no usable speed to extrapolate from.
 */
function getCurrentState(track: Track): CurrentState | null {
  const coords = track.path?.coordinates;
  if (!coords || coords.length < 2) return null;

  const lastIdx = coords.length - 1;
  const [lon, lat] = coords[lastIdx];

  const cogDeg = deriveCurrentCourse(coords);

  // Prefer the last segment's speed (waypoint before the final coord), falling
  // back to the final waypoint if needed.
  const wps = track.waypoints ?? [];
  const lastWp = wps[lastIdx];
  const prevWp = wps[lastIdx - 1];
  const sogKn = pickFinite(prevWp?.sog_kn, lastWp?.sog_kn) ?? 0;

  if (sogKn <= 0) return null;

  // Use an explicit rate of turn when the source provides one; otherwise derive
  // it from the recent course history (AIS track feeds usually omit ROT).
  const explicitRot = pickFinite(lastWp?.rot_deg_min, prevWp?.rot_deg_min) ?? 0;
  const rotDerived = Math.abs(explicitRot) <= 1e-6;
  const rotDegMin = rotDerived
    ? deriveTurnRateDegMin(coords, wps)
    : explicitRot;

  return { lon, lat, cogDeg, sogKn, rotDegMin, rotDerived };
}

/** Normalise a degree delta to the signed range (-180, 180]. */
function normalizeSignedDeg(deg: number): number {
  return ((((deg + 180) % 360) + 360) % 360) - 180;
}

/**
 * Estimate the current rate of turn (deg/min) from a track's recent course
 * history. Builds per-segment bearings (ignoring near-zero-length segments as
 * direction noise), then measures the total signed bearing change over the most
 * recent time window divided by its duration. Returns 0 when there isn't enough
 * signal (too few usable segments or no elapsed time).
 */
function deriveTurnRateDegMin(
  coords: number[][],
  waypoints: WaypointAttributes[],
): number {
  const segments: { bearing: number; timeMs: number }[] = [];
  for (let i = 0; i < coords.length - 1; i++) {
    const [lon1, lat1] = coords[i];
    const [lon2, lat2] = coords[i + 1];
    if (haversineDistance(lon1, lat1, lon2, lat2) < ROT_MIN_SEGMENT_M) continue;

    const t1 = Date.parse(waypoints[i]?.timestamp ?? '');
    const t2 = Date.parse(waypoints[i + 1]?.timestamp ?? '');
    if (!Number.isFinite(t1) || !Number.isFinite(t2)) continue;

    segments.push({
      bearing: calculateBearing(lon1, lat1, lon2, lat2),
      timeMs: (t1 + t2) / 2,
    });
  }

  if (segments.length < 2) return 0;

  const lastTimeMs = segments[segments.length - 1].timeMs;
  const windowMs = ROT_DERIVATION_WINDOW_SEC * 1000;

  // Walk back to the earliest segment still inside the time window, but always
  // keep at least two segments so a turn can be measured.
  let startIdx = segments.length - 2;
  while (
    startIdx > 0 &&
    lastTimeMs - segments[startIdx - 1].timeMs <= windowMs
  ) {
    startIdx--;
  }

  let totalChange = 0;
  for (let i = startIdx + 1; i < segments.length; i++) {
    totalChange += normalizeSignedDeg(
      segments[i].bearing - segments[i - 1].bearing,
    );
  }

  const dtMin =
    (segments[segments.length - 1].timeMs - segments[startIdx].timeMs) / 60000;
  if (dtMin <= 0) return 0;

  const rot = totalChange / dtMin;
  return Math.max(-ROT_MAX_DEG_MIN, Math.min(ROT_MAX_DEG_MIN, rot));
}

function pickFinite(...values: (number | undefined)[]): number | undefined {
  for (const v of values) {
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return undefined;
}

/**
 * Make a polyline's longitudes continuous so it doesn't break across the
 * antimeridian. Each point is shifted by whole turns of 360° to stay within
 * 180° of the previous one; after projecting, adjacent vertices then stay
 * adjacent instead of jumping across the whole map.
 */
function unwrapLongitudes(path: LonLat[]): LonLat[] {
  if (path.length === 0) return path;
  const out: LonLat[] = [path[0]];
  let prevLon = path[0][0];
  for (let i = 1; i < path.length; i++) {
    let lon = path[i][0];
    while (lon - prevLon > 180) lon -= 360;
    while (lon - prevLon < -180) lon += 360;
    out.push([lon, path[i][1]]);
    prevLon = lon;
  }
  return out;
}

/**
 * Straight SOG/COG vector from the track's last position: a two-point line
 * along the current course with length `SOG × duration`.
 *
 * Returns the line as [start, end] in [lon, lat], or null when it can't be
 * computed (track too short, or vessel stopped).
 */
export function computeSogVector(
  track: Track,
  durationSec: number = DEFAULT_SOG_VECTOR_DURATION_SEC,
): LonLat[] | null {
  const state = getCurrentState(track);
  if (!state || durationSec <= 0) return null;

  const distanceM = (state.sogKn * METERS_PER_NM * durationSec) / 3600;
  const end = destinationPoint(state.lon, state.lat, state.cogDeg, distanceM);
  return unwrapLongitudes([[state.lon, state.lat], end]);
}

/**
 * Curved predictor path from the track's last position. Integrates the rate of
 * turn so the heading changes over time, sampling the path into a polyline.
 *
 * When the track has no explicit rate of turn (typical for AIS feeds that only
 * carry COG/SOG), the turn rate is derived from the recent course history.
 *
 * Returns the polyline in [lon, lat], or null when it can't be computed.
 */
export function computePredictorPath(
  track: Track,
  durationSec: number = DEFAULT_PREDICTOR_DURATION_SEC,
): LonLat[] | null {
  const state = getCurrentState(track);
  if (!state || durationSec <= 0) return null;

  const steps = Math.min(
    PREDICTOR_MAX_STEPS,
    Math.max(PREDICTOR_MIN_STEPS, Math.ceil(durationSec / PREDICTOR_STEP_SEC)),
  );
  const dtSec = durationSec / steps;
  // Distance covered per step (constant speed).
  const stepDistanceM = (state.sogKn * METERS_PER_NM * dtSec) / 3600;
  // Heading change per step from rate of turn (deg/min → deg/step).
  const headingDeltaPerStep = state.rotDegMin * (dtSec / 60);

  const path: LonLat[] = [[state.lon, state.lat]];
  let { lon, lat } = state;
  let heading = state.cogDeg;

  for (let i = 0; i < steps; i++) {
    // Apply turn at the midpoint of the step for a smoother arc.
    heading += headingDeltaPerStep / 2;
    [lon, lat] = destinationPoint(lon, lat, heading, stepDistanceM);
    heading += headingDeltaPerStep / 2;
    path.push([lon, lat]);
  }

  return unwrapLongitudes(path);
}
